<template>
  <form class="conversation--form" @submit.prevent="onFormSubmit">
    <div v-if="showNoInboxAlert" class="callout warning">
      <p>
        {{ $t('NEW_CONVERSATION.NO_INBOX') }}
      </p>
    </div>

    <div v-else>
      <div class="row gutter-small">
        <div class="columns">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
          </label>

          <div class="multiselect-wrap--small">
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              :close-on-select="true"
              :options="[...inboxes]"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <inbox-dropdown-item
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>

              <template slot="option" slot-scope="{ option }">
                <inbox-dropdown-item
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>

          <label :class="{ error: $v.targetInbox.$error }">
            <span v-if="$v.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>

        <div class="columns">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.TO.LABEL') }}

            <div class="contact-input">
              <thumbnail
                :src="contact.thumbnail"
                size="24px"
                :username="contact.name"
                :status="contact.availability_status"
              />

              <h4 class="text-block-title contact-name">
                {{ contact.name }}
              </h4>
            </div>
          </label>
        </div>
      </div>

      <div v-if="contact.blocked_contact" class="alert-filter wide-alert">
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">
          <h3>
            {{ $t('NEW_CONVERSATION.BLOCKED_CONTACT_ALERT.WARNING') }}
          </h3>

          <p v-html="$t('NEW_CONVERSATION.BLOCKED_CONTACT_ALERT.TEXT')"></p>
        </div>
      </div>

      <div
        v-else-if="contact.blocked_from_templates"
        class="alert-filter wide-alert"
      >
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">
          <h3>
            {{ $t('NEW_CONVERSATION.BLOCKED_TEMPLATES_ALERT.WARNING') }}
          </h3>

          <p v-html="$t('NEW_CONVERSATION.BLOCKED_TEMPLATES_ALERT.TEXT')"></p>
        </div>
      </div>

      <div v-else-if="whatsappTemplatesUiFlags.isFetchingBlockedPerLimit">
        <spinner :colorScheme="'dark'" />
      </div>
      <div
        v-else-if="messageTemplateLimitExceeded"
        class="alert-filter wide-alert"
      >
        <div class="icon-container">
          <fluent-icon icon="warning" size="40" />
        </div>

        <div class="alert-text">          
          <p v-html="messageTemplateLimitExceeded"></p>
        </div>
      </div>

      <div v-else>
        <div
          v-if="
            !isInboxInactive &&
            contact.additional_attributes &&
            contact.additional_attributes.inbox_id &&
            targetInbox
          "
          class="alert-filter wide-alert"
        >
          <div class="icon-container">
            <fluent-icon icon="warning" size="40" />
          </div>

          <div class="alert-text">
            <h3>{{ $t('NEW_CONVERSATION.ALERT.WARNING') }}</h3>

            <p
              v-html="
                $t('NEW_CONVERSATION.ALERT.TEXT', {
                  inbox: initialInboxName
                    ? initialInboxName
                    : $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER'),
                })
              "
            ></p>
          </div>
        </div>

        <div v-if="isInboxInactive" class="alert-filter wide-alert">
          <div class="icon-container">
            <fluent-icon icon="warning" size="40" />
          </div>

          <div class="alert-text">
            <h3>
              {{ $t('NEW_CONVERSATION.INACTIVE_INBOX_ALERT.WARNING') }}
            </h3>

            <p v-html="$t('NEW_CONVERSATION.INACTIVE_INBOX_ALERT.TEXT')"></p>
          </div>
        </div>

        <template v-if="!isInboxInactive">
          <div v-if="isAnEmailInbox" class="row">
            <div class="columns">
              <label :class="{ error: $v.subject.$error }">
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}

                <input
                  v-model="subject"
                  type="text"
                  :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
                  @input="$v.subject.$touch"
                />

                <span v-if="$v.subject.$error" class="message">
                  {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
                </span>
              </label>
            </div>
          </div>

          <div class="row">
            <div class="columns">
              <div class="canned-response">
                <canned-response
                  v-if="showCannedResponseMenu && hasSlashCommand"
                  :search-key="cannedResponseSearchKey"
                  :inbox-id="targetInboxId"
                  @click="replaceTextWithCannedResponse"
                />
              </div>

              <div v-if="isEmailOrWebWidgetInbox">
                <label>
                  {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}

                  <reply-email-head
                    v-if="isAnEmailInbox"
                    :cc-emails.sync="ccEmails"
                    :bcc-emails.sync="bccEmails"
                  />

                  <label class="editor-wrap">
                    <woot-message-editor
                      v-model="message"
                      class="message-editor"
                      :class="{ editor_warning: $v.message.$error }"
                      :inbox-id="targetInboxId"
                      :enable-variables="true"
                      :placeholder="
                        $t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')
                      "
                      @toggle-canned-menu="toggleCannedMenu"
                      @blur="$v.message.$touch"
                    />

                    <span
                      v-if="$v.message.$error"
                      class="editor-warning__message"
                    >
                      {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
                    </span>
                  </label>
                </label>
              </div>

              <div v-else-if="hasWhatsappTemplates">
                <p class="margin-bottom-2" v-if="targetInbox">
                  <label
                    v-if="!targetInbox.lock_to_single_conversation"
                    class="margin-top-2"
                  >
                    <input
                      type="checkbox"
                      v-model="ignoreExistingConversation"
                    />

                    {{
                      $t('NEW_CONVERSATION.FORM.IGNORE_EXISTING_CONVERSATION')
                    }}
                  </label>

                  <small v-else>
                    {{
                      $t(
                        'NEW_CONVERSATION.FORM.INBOX.INFO_LOCK_TO_SINGLE_CONVERSATION_ENABLED'
                      )
                    }}
                  </small>
                </p>

                <whatsapp-templates
                  :inbox-id="targetInbox.id"
                  :templates-admin="targetInbox.templates_admin"
                  @on-select-template="toggleWaTemplate"
                  @on-send="onSendWhatsAppReply"
                />
              </div>

              <label v-else :class="{ error: $v.message.$error }">
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}

                <textarea
                  v-model="message"
                  class="message-input"
                  :placeholder="
                    targetInbox && targetInbox.id
                      ? $t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')
                      : $t('NEW_CONVERSATION.FORM.INBOX.ERROR')
                  "
                  :readonly="!(targetInbox && targetInbox.id)"
                  @input="$v.message.$touch"
                />

                <span v-if="$v.message.$error" class="message">
                  {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
                </span>
              </label>
            </div>
          </div>

          <div v-if="!hasWhatsappTemplates" class="modal-footer">
            <button class="button clear" @click.prevent="onCancel">
              {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
            </button>

            <woot-button
              type="submit"
              :is-loading="conversationsUiFlags.isCreating || isSubmitting"
              :is-disabled="isSubmitting"
            >
              {{ $t('NEW_CONVERSATION.FORM.SUBMIT') }}
            </woot-button>
          </div>
        </template>
      </div>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead';
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem';
import WhatsappTemplates from './WhatsappTemplates.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { getInboxSource } from 'dashboard/helper/inbox';
import { required, requiredIf } from 'vuelidate/lib/validators';
import Input from '../../../../components/widgets/forms/Input.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    Thumbnail,
    WootMessageEditor,
    ReplyEmailHead,
    CannedResponse,
    WhatsappTemplates,
    InboxDropdownItem,
    Input,
    Spinner,
  },
  mixins: [alertMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      name: '',
      subject: '',
      message: '',
      showCannedResponseMenu: false,
      cannedResponseSearchKey: '',
      bccEmails: '',
      ccEmails: '',
      targetInbox: {},
      whatsappTemplateSelected: false,
      conversationId: null,
      initialInboxName: null,
      ignoreExistingConversation: false,
      isSubmitting: false      
    };
  },
  validations: {
    subject: {
      required: requiredIf('isAnEmailInbox'),
    },
    message: {
      required,
    },
    targetInbox: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      getInboxes: 'inboxes/getInboxes',
      currentAccount: 'getCurrentAccount',
      currentUserAccount: 'getCurrentUserAccount',
      blockedSendTemplates: 'whatsappTemplates/getBlockedSendTemplates',
      whatsappTemplatesUiFlags: 'whatsappTemplates/getUIFlags',
    }),
    targetInboxId() {
      return this.targetInbox?.id || null;
    },
    isInboxInactive() {
      return this.targetInbox && this.targetInbox.is_inbox_active === false;
    },
    emailMessagePayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content: this.message },
        mailSubject: this.subject,
        assigneeId: this.currentUser.id,
      };
      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }

      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }
      return payload;
    },
    showNoInboxAlert() {
      if (!this.contact.contactableInboxes) {
        return false;
      }
      return this.inboxes.length === 0 && !this.uiFlags.isFetchingInboxes;
    },
    inboxes() {
      let inboxList = this.contact.contactableInboxes || [];
      inboxList = inboxList.map(inbox => ({
        ...inbox.inbox,
        sourceId: inbox.source_id,
      }));

      if (!inboxList.length) {
        inboxList = this.getInboxes.map(inbox => ({
          ...inbox,
          sourceId: (this.contact.phone_number && this.contact.phone_number.trim() != '' ?  this.contact.phone_number.replace('+', '') : this.contact?.bsuid_user_id)
        }));
      }

      return inboxList;
    },
    isAnEmailInbox() {
      return (
        this.targetInbox && this.targetInbox.channel_type === INBOX_TYPES.EMAIL
      );
    },
    isAnWebWidgetInbox() {
      return (
        this.targetInbox && this.targetInbox.channel_type === INBOX_TYPES.WEB
      );
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailInbox || this.isAnWebWidgetInbox;
    },
    hasWhatsappTemplates() {
      if (this.isWhatsappWeb) {
        return false;
      }
      return (
        !!this.targetInbox?.message_templates &&
        !this.contact?.blocked_from_templates
      );
    },
    isWhatsappWeb() {
      const provider = this.targetInbox?.provider;
      return provider && provider == 'whatsapp_web';
    },
    messageTemplateLimitExceeded(){
      if(!this.blockedSendTemplates || !this.currentAccount || !this.currentUserAccount || Object.keys(this.blockedSendTemplates).length === 0){
        return null;
      }
      if(this.blockedSendTemplates?.user.daily){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.AGENT.DAILY', { limit: this.currentUserAccount?.daily_template_limit });
      }
      if(this.blockedSendTemplates?.user.monthly){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.AGENT.MONTHLY', { limit: this.currentUserAccount?.monthly_template_limit });
      }
      if(this.blockedSendTemplates?.account.daily){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.ACCOUNT.DAILY', { limit: this.currentAccount?.daily_template_limit });
      }
      if(this.blockedSendTemplates.account.monthly){
        return this.$t('CONVERSATION.BLOCKED_TEMPLATE_LIMIT.ACCOUNT.MONTHLY', { limit: this.currentAccount?.monthly_template_limit });
      }
      return null;
    }
  },
  watch: {
    message(value) {
      this.hasSlashCommand = value[0] === '/' && !this.isEmailOrWebWidgetInbox;
      const hasNextWord = value.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.cannedResponseSearchKey = value.substring(1);
        this.showCannedResponseMenu = true;
      } else {
        this.cannedResponseSearchKey = '';
        this.showCannedResponseMenu = false;
      }
    },
    targetInbox: {
      handler(newInbox) {
        if (newInbox && newInbox.name && !this.initialInboxName) {
          this.initialInboxName = newInbox.name;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setInitialInbox();    
    if(this.currentAccount?.enable_template_limit || this.currentUserAccount.enable_template_limit){
      this.$store.dispatch('whatsappTemplates/getBlockedSendTemplates');
    }
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    replaceTextWithCannedResponse(message) {
      this.message = message;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    prepareWhatsAppMessagePayload({ message: content, templateParams }) {
      let payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content, template_params: templateParams },
        assigneeId: this.currentUser.id,
      };
      if (!this.targetInbox.lock_to_single_conversation) {
        payload.ignoreExistingConversation = this.ignoreExistingConversation;
      }
      return payload;
    },
    onFormSubmit() {
      if (this.isSubmitting) return;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (
        this.isAnEmailInbox ||
        this.isAnWebWidgetInbox ||
        this.isEmailOrWebWidgetInbox
      ) {
        this.isSubmitting = true;
        this.createConversation(this.emailMessagePayload);
      }
    },
    async createConversation(payload) {
      try {
        const data = await this.onSubmit(payload);
        this.conversationId = data.id;
        const action = {
          type: 'link',
          to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
          message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        };
        this.onSuccess();
        this.showAlert(
          this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'),
          action
        );
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },

    toggleWaTemplate(val) {
      this.whatsappTemplateSelected = val;
    },
    async onSendWhatsAppReply(messagePayload) {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        if (messagePayload.private) {
          this.sendAttachmentPrivateMessage(messagePayload);
        } else {
          const payload = this.prepareWhatsAppMessagePayload(messagePayload);
          await this.createConversation(payload);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    sendAttachmentPrivateMessage(messagePayload) {
      if (typeof conversationIdCheckins != 'undefined') {
        clearInterval(conversationIdCheckins);
      }

      const conversationIdCheckins = setInterval(async () => {
        if (this.conversationId) {
          clearInterval(conversationIdCheckins);
          await this.$store.dispatch('createPendingMessageAndSend', {
            ...messagePayload,
            conversationId: this.conversationId,
          });

          this.conversationId = null;
        }
      }, 1000);
    },
    inboxReadableIdentifier(inbox) {
      return `${inbox.name} (${inbox.channel_type})`;
    },
    computedInboxSource(inbox) {
      if (!inbox.channel_type) return '';
      const classByType = getInboxSource(
        inbox.channel_type,
        inbox.phone_number,
        inbox
      );
      return classByType;
    },
    setInitialInbox() {
      const inboxId = this.contact.additional_attributes?.inbox_id;
      let matchedInbox = null;
      if (inboxId) {
        matchedInbox = this.inboxes.find(inbox => inbox.id === inboxId);
      }
      if (matchedInbox || this.inboxes.length == 1) {
        this.targetInbox = matchedInbox || this.inboxes[0];
      }
    },
  },
};
</script>

<style scoped lang="scss">
.conversation--form {
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.canned-response {
  position: relative;
}

.input-group-label {
  font-size: var(--font-size-small);
}

.contact-input {
  display: flex;
  align-items: center;
  height: 3.9rem;
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  padding: var(--space-smaller) var(--space-small);
  border-radius: var(--border-radius-small);

  .contact-name {
    margin: 0;
    margin-left: var(--space-small);
    margin-right: var(--space-small);
  }
}

.message-input {
  min-height: 8rem;
  &[readonly] {
    background: none;
    text-align: center;
  }
}

.row.gutter-small {
  gap: var(--space-small);
}

::v-deep {
  .mention--box {
    left: 0;
    margin: auto;
    right: 0;
    top: unset;
    height: fit-content;
  }

  /* TODO: Remove when have standardized a component out of multiselect  */
  .multiselect .multiselect__content .multiselect__option span {
    display: inline-flex;
    width: var(--space-medium);
    color: var(--s-600);
  }
  .multiselect .multiselect__content .multiselect__option {
    padding: var(--space-micro) var(--space-smaller);
  }
}

.wide-alert {
  width: 100%;
  max-width: none;
  padding-right: 50px;
  display: flex;
}

.alert-filter {
  width: 100%;
  max-width: none;
  flex: 1 1 auto !important;
  display: flex;
  align-items: center;
  padding: var(--space-small);
  background: var(--r-50);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-medium);
  margin-right: -50px;

  .icon-container {
    flex-shrink: 0;
    margin-right: var(--space-small);

    svg {
      color: var(--r-800);
    }
  }

  .alert-text {
    flex-grow: 1;

    h3 {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-black);
    }

    p {
      font-size: var(--font-size-mini);
      line-height: 1.5;
      margin-bottom: 0;
    }
  }
}
</style>
